import {
    AuditError,
    KNOWN_AUDIT_CODES,
    type AuditAggregateBucket,
    type AuditAggregateField,
    type AuditAggregateGranularity,
    type AuditAggregateQuery,
    type AuditAggregateResult,
    type AuditContentMode,
    type AuditEntry,
    type AuditErrorCode,
    type AuditQueryFilter,
    type AuditStatus,
} from '@/domain/audit'
import type { AuditApi } from '@/ports/audit-api'

import { appendActiveTenant } from './_active-tenant'

/**
 * HttpAuditApi : adapter fetch des endpoints `/api/v1/audit/*` du
 * control-plane. Les méthodes utilisées sont GET (idempotent) donc PAS
 * de CSRF nécessaire — juste `credentials: 'include'` pour envoyer le
 * cookie de session.
 *
 * Normalisation des erreurs en `AuditError`, parsing strict des entries
 * (toute valeur inattendue → `AuditError('internal_error')` plutôt
 * qu'un type-coerce silencieux).
 */
export class HttpAuditApi implements AuditApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async query(filter: AuditQueryFilter): Promise<AuditEntry[]> {
        const params = buildQueryString(filter)
        const path = '/api/v1/audit/query' + (params ? '?' + params : '')
        const resp = await this.send(path)
        if (!resp.ok) {
            throw await readAuditError(resp)
        }
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new AuditError('internal_error', 'Invalid /audit/query response shape')
        }
        return body.items.map(parseEntry)
    }

    async aggregate(query: AuditAggregateQuery): Promise<AuditAggregateResult> {
        const params = new URLSearchParams()
        params.set('field', query.field)
        params.set('granularity', query.granularity)
        if (query.groupBy && query.groupBy.length > 0) {
            params.set('group_by', query.groupBy.join(','))
        }
        appendFilterParams(params, query.filter)
        appendActiveTenant(params)

        const resp = await this.send('/api/v1/audit/aggregate?' + params.toString())
        if (!resp.ok) {
            throw await readAuditError(resp)
        }
        const body = (await resp.json()) as {
            buckets?: unknown
            field?: unknown
            granularity?: unknown
        }
        if (!Array.isArray(body.buckets)) {
            throw new AuditError('internal_error', 'Invalid /audit/aggregate response shape')
        }
        const buckets = body.buckets.map(parseBucket)
        return {
            buckets,
            field: stringField(body.field) as AuditAggregateField,
            granularity: stringField(body.granularity) as AuditAggregateGranularity,
        }
    }

    private send(path: string): Promise<Response> {
        return fetch(this.baseURL + path, {
            method: 'GET',
            credentials: 'include',
            // Pas de cache : les dashboards veulent du frais.
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new AuditError('network_error', message)
        })
    }
}

/**
 * buildQueryString crée la query string pour /audit/query depuis le filter.
 */
function buildQueryString(filter: AuditQueryFilter): string {
    const params = new URLSearchParams()
    appendFilterParams(params, filter)
    appendActiveTenant(params)
    if (filter.limit !== undefined && filter.limit > 0) {
        params.set('limit', String(filter.limit))
    }
    if (filter.offset !== undefined && filter.offset > 0) {
        params.set('offset', String(filter.offset))
    }
    return params.toString()
}

function appendFilterParams(params: URLSearchParams, filter: AuditQueryFilter): void {
    if (filter.tenantId) {
        params.set('tenant_id', filter.tenantId)
    }
    if (filter.from) {
        params.set('from', filter.from)
    }
    if (filter.to) {
        params.set('to', filter.to)
    }
    if (filter.status) {
        params.set('status', filter.status)
    }
    if (filter.provider) {
        params.set('provider', filter.provider)
    }
    if (filter.model) {
        params.set('model', filter.model)
    }
    if (filter.teamId) {
        params.set('team_id', filter.teamId)
    }
    if (filter.userId) {
        params.set('user_id', filter.userId)
    }
    if (filter.apiKeyId) {
        params.set('api_key_id', filter.apiKeyId)
    }
    if (filter.projectTag) {
        params.set('project_tag', filter.projectTag)
    }
    if (filter.carbonZone) {
        params.set('carbon_zone', filter.carbonZone)
    }
    if (filter.authMode) {
        params.set('auth_mode', filter.authMode)
    }
}

async function readAuditError(resp: Response): Promise<AuditError> {
    // 401 / 403 spécifiques — l'API retourne ces statuts via le middleware
    // AuthSession / RequireCSRFToken, parfois sans un shape "error" usuel.
    if (resp.status === 401) {
        return new AuditError('unauthorized', 'Session expired or missing', 401)
    }
    if (resp.status === 403) {
        return new AuditError('forbidden', 'Forbidden', 403)
    }
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        return new AuditError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new AuditError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: AuditErrorCode = (KNOWN_AUDIT_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as AuditErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new AuditError(code, message, resp.status)
}

/**
 * parseEntry transforme un JSON brut en AuditEntry. Strict : tout
 * littéral inattendu (status, content_mode) déclenche une erreur.
 */
function parseEntry(raw: unknown): AuditEntry {
    if (!raw || typeof raw !== 'object') {
        throw new AuditError('internal_error', 'Invalid audit entry payload')
    }
    const obj = raw as Record<string, unknown>
    const status = parseStatus(stringField(obj.status))
    const contentMode = parseContentMode(stringField(obj.content_mode))
    if (!status || !contentMode) {
        throw new AuditError('internal_error', 'Invalid audit entry: status or content_mode')
    }
    return {
        timestamp: stringField(obj.timestamp),
        requestId: stringField(obj.request_id),
        tenantId: stringField(obj.tenant_id),
        userId: stringField(obj.user_id),
        apiKeyId: stringField(obj.api_key_id),
        modelRequested: stringField(obj.model_requested),
        modelUsed: stringField(obj.model_used),
        providerUsed: stringField(obj.provider_used),
        tokensInput: numberField(obj.tokens_input),
        tokensOutput: numberField(obj.tokens_output),
        cacheReadInputTokens: numberField(obj.cache_read_input_tokens),
        cacheCreationInputTokens: numberField(obj.cache_creation_input_tokens),
        costEUR: numberField(obj.cost_eur_estimated),
        energyKwh: numberField(obj.energy_kwh),
        gwpKgCO2e: numberField(obj.gwp_kgco2e),
        gwpKgCO2eMin: numberField(obj.gwp_kgco2e_min),
        gwpKgCO2eMax: numberField(obj.gwp_kgco2e_max),
        adpeKgSbe: numberField(obj.adpe_kgsbe),
        peMJ: numberField(obj.pe_mj),
        waterL: numberField(obj.water_l),
        carbonZone: stringField(obj.carbon_zone),
        latencyTotalMs: numberField(obj.latency_total_ms),
        latencyProviderMs: numberField(obj.latency_provider_ms),
        latencyPreLLMMs: numberField(obj.latency_prellm_ms),
        ipSource: stringField(obj.ip_source),
        userAgent: stringField(obj.user_agent),
        status,
        errorType: stringField(obj.error_type),
        piiCount: numberField(obj.pii_count),
        injectionScore: numberField(obj.injection_score),
        injectionAction: stringField(obj.injection_action),
        safetyCategories: stringArrayField(obj.safety_categories),
        fallbackTriggered: boolField(obj.fallback_triggered),
        fallbackReason: stringField(obj.fallback_reason),
        quotaState: stringField(obj.quota_state),
        contentMode,
        promptHash: stringField(obj.prompt_hash),
        authMode: parseAuthMode(obj.auth_mode),
        passthroughProvider: parsePassthroughProvider(obj.passthrough_provider),
        providerQuotaRemainingRequests: optionalNumberField(obj.provider_quota_remaining_requests),
        providerQuotaRemainingTokens: optionalNumberField(obj.provider_quota_remaining_tokens),
        providerQuotaResetAt: optionalStringField(obj.provider_quota_reset_at),
        providerQuotaTokensResetAt: optionalStringField(obj.provider_quota_tokens_reset_at),
        providerQuotaInputTokensRemaining: optionalNumberField(obj.provider_quota_input_tokens_remaining),
        providerQuotaOutputTokensRemaining: optionalNumberField(obj.provider_quota_output_tokens_remaining),
    }
}

function parseAuthMode(raw: unknown): 'api_key' | 'passthrough' {
    return raw === 'passthrough' ? 'passthrough' : 'api_key'
}

function parsePassthroughProvider(raw: unknown): '' | 'anthropic_oauth' | 'openai_codex' | 'github_copilot' {
    if (raw === 'anthropic_oauth' || raw === 'openai_codex' || raw === 'github_copilot') {
        return raw
    }
    return ''
}

function optionalNumberField(v: unknown): number | undefined {
    if (typeof v !== 'number' || !Number.isFinite(v)) return undefined
    return v
}

function optionalStringField(v: unknown): string | undefined {
    return typeof v === 'string' && v ? v : undefined
}

function parseBucket(raw: unknown): AuditAggregateBucket {
    if (!raw || typeof raw !== 'object') {
        throw new AuditError('internal_error', 'Invalid aggregate bucket')
    }
    const obj = raw as Record<string, unknown>
    const groupsRaw = obj.groups
    let groups: Readonly<Record<string, string>> | undefined
    if (groupsRaw && typeof groupsRaw === 'object') {
        const flat: Record<string, string> = {}
        for (const [k, v] of Object.entries(groupsRaw)) {
            if (typeof v === 'string') {
                flat[k] = v
            }
        }
        groups = flat
    }
    return {
        timestamp: stringField(obj.timestamp),
        groups,
        value: numberField(obj.value),
    }
}

function parseStatus(raw: string): AuditStatus | null {
    return raw === 'success' || raw === 'blocked' || raw === 'error' ? raw : null
}

function parseContentMode(raw: string): AuditContentMode | null {
    return raw === 'zero' || raw === 'tokenized' || raw === 'full' ? raw : null
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

function numberField(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function boolField(v: unknown): boolean {
    return typeof v === 'boolean' ? v : false
}

function stringArrayField(v: unknown): string[] {
    return Array.isArray(v) ? v.filter((item): item is string => typeof item === 'string') : []
}
